/**
 * Bench Report Viewer
 *
 * Displays and allows editing of reports within the Analyst Bench context.
 * Combines viewing and editing functionality for analysts.
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { AnalystBenchProvider, findInstitutionById } from '../../context/AnalystBenchContext';
import ReportPreview from '../../components/report/ReportPreview';
import type { BackendReportData } from '../../utils/reportDataTransformers';
import {
  ReportMetadata,
  getReportContent,
  getReportMetadata,
  downloadReportCSV,
  formatReportDate,
  formatFileSize,
  formatTokenCount,
} from '../../services/reportService';
import { exportReportToPDF } from '../../utils/exportPDF';
import './BenchReportViewer.css';

function BenchReportViewerContent() {
  const { institutionId, reportId } = useParams<{ institutionId: string; reportId: string }>();
  const navigate = useNavigate();
  const { userProfile } = useAuth();

  const institution = findInstitutionById(institutionId || '');

  const [reportData, setReportData] = useState<BackendReportData | null>(null);
  const [metadata, setMetadata] = useState<ReportMetadata | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showTokenDetails, setShowTokenDetails] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showCsvDropdown, setShowCsvDropdown] = useState(false);

  const isPlatformAdmin = userProfile?.role === 'platform_admin';

  useEffect(() => {
    if (reportId) {
      loadReport();
    }
  }, [reportId]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.csv-dropdown')) {
        setShowCsvDropdown(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const loadReport = async () => {
    if (!reportId) return;

    try {
      setLoading(true);
      setError(null);

      const [content, meta] = await Promise.all([
        getReportContent(reportId),
        getReportMetadata(reportId),
      ]);

      // Transform the content to BackendReportData format
      const backendData: BackendReportData = {
        metadata: {
          institution_id: content.metadata.institution_id,
          period: content.metadata.period,
          generation_date: content.metadata.generation_date,
        },
        data: {
          currentIndicators: content.data.currentIndicators || [],
          currentBalance: content.data.currentBalance || [],
          historicalIndicators: content.data.historicalIndicators || [],
          historicalBalance: content.data.historicalBalance || [],
        },
        narratives: content.narratives || {},
      };

      setReportData(backendData);
      setMetadata(meta);
    } catch (err) {
      console.error('Error loading report:', err);
      setError('Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (institutionId) {
      navigate(`/analyst-bench/${institutionId}`);
    } else {
      navigate('/analyst-bench');
    }
  };

  const handleDownloadCSV = (type: 'indicators' | 'balance_sheet' | 'historical_indicators' | 'historical_balance') => {
    if (reportId) {
      downloadReportCSV(reportId, type);
      setShowCsvDropdown(false);
    }
  };

  const handleExportPDF = async () => {
    if (!metadata) return;

    try {
      setExporting(true);
      await exportReportToPDF(
        metadata.institution_id,
        metadata.period
      );
    } catch (err) {
      console.error('Error exporting PDF:', err);
      alert('Error exporting PDF. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const formatPeriod = (period: string) => {
    const [year, month] = period.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="bench-report-viewer">
        <div className="bench-report-loading">
          <div className="loading-spinner" />
          <span>Loading report...</span>
        </div>
      </div>
    );
  }

  if (error || !reportData || !metadata) {
    return (
      <div className="bench-report-viewer">
        <div className="bench-report-error">
          <h2>Error Loading Report</h2>
          <p>{error || 'Report not found'}</p>
          <button onClick={handleBack} className="btn-back">
            Back to Workbench
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bench-report-viewer">
      {/* Header */}
      <header className="bench-report-header">
        <div className="header-left">
          <button onClick={handleBack} className="btn-back">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
            <span>Back to Workbench</span>
          </button>
        </div>

        <div className="header-center">
          <h1>{institution?.name || institutionId}</h1>
          <span className="report-period">{formatPeriod(metadata.period)}</span>
        </div>

        <div className="header-right">
          {/* PDF Export Button */}
          <button
            className="btn-primary"
            onClick={handleExportPDF}
            disabled={exporting}
          >
            {exporting ? (
              <>
                <div className="btn-spinner" />
                Exporting...
              </>
            ) : (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <path d="M14 2v6h6"/>
                  <path d="M12 18v-6"/>
                  <path d="M9 15l3 3 3-3"/>
                </svg>
                Export PDF
              </>
            )}
          </button>

          {/* CSV Export Dropdown */}
          <div className="csv-dropdown">
            <button
              className="btn-secondary dropdown-trigger"
              onClick={(e) => {
                e.stopPropagation();
                setShowCsvDropdown(!showCsvDropdown);
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <path d="M14 2v6h6"/>
                <path d="M8 13h8"/>
                <path d="M8 17h8"/>
              </svg>
              Export CSV
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M6 9l6 6 6-6"/>
              </svg>
            </button>
            {showCsvDropdown && (
              <div className="dropdown-menu">
                <button onClick={() => handleDownloadCSV('indicators')}>
                  Financial Indicators
                </button>
                <button onClick={() => handleDownloadCSV('balance_sheet')}>
                  Balance Sheet
                </button>
                <button onClick={() => handleDownloadCSV('historical_indicators')}>
                  Historical Indicators
                </button>
                <button onClick={() => handleDownloadCSV('historical_balance')}>
                  Historical Balance
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Metadata Bar */}
      <div className="bench-report-meta">
        <div className="meta-item">
          <span className="meta-label">Generated</span>
          <span className="meta-value">{formatReportDate(metadata.generated_at)}</span>
        </div>
        <div className="meta-item">
          <span className="meta-label">Size</span>
          <span className="meta-value">{formatFileSize(metadata.file_size_bytes)}</span>
        </div>
        <div className="meta-item">
          <span className="meta-label">Sections</span>
          <span className="meta-value">{metadata.sections_included.length}</span>
        </div>
        <div className="meta-item">
          <span className="meta-label">Status</span>
          <span className={`meta-value status-badge status-${metadata.status}`}>
            {metadata.status === 'completed' ? 'Published' : metadata.status}
          </span>
        </div>
        {metadata.version && (
          <div className="meta-item">
            <span className="meta-label">Version</span>
            <span className="meta-value">v{metadata.version}</span>
          </div>
        )}
        {metadata.report_type && (
          <div className="meta-item">
            <span className="meta-label">Type</span>
            <span className={`meta-value type-badge type-${metadata.report_type}`}>
              {metadata.report_type === 'executive_summary' ? 'Executive Summary' : 'Full Report'}
            </span>
          </div>
        )}
        {isPlatformAdmin && metadata.token_usage && (
          <div
            className="meta-item meta-item-clickable"
            onClick={() => setShowTokenDetails(!showTokenDetails)}
          >
            <span className="meta-label">Tokens</span>
            <span className="meta-value">
              {formatTokenCount(metadata.token_usage.total_tokens)} total
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginLeft: '4px' }}>
                <path d={showTokenDetails ? "M18 15l-6-6-6 6" : "M6 9l6 6 6-6"}/>
              </svg>
            </span>
          </div>
        )}
      </div>

      {/* Token Details (Admin Only) */}
      {isPlatformAdmin && showTokenDetails && metadata.token_usage && (
        <div className="token-details-panel">
          <h3>Token Usage Details</h3>
          <div className="token-summary">
            <div className="token-stat">
              <span className="token-stat-label">Input Tokens</span>
              <span className="token-stat-value">
                {formatTokenCount(metadata.token_usage.total_input_tokens)}
              </span>
            </div>
            <div className="token-stat">
              <span className="token-stat-label">Output Tokens</span>
              <span className="token-stat-value">
                {formatTokenCount(metadata.token_usage.total_output_tokens)}
              </span>
            </div>
            <div className="token-stat">
              <span className="token-stat-label">Total</span>
              <span className="token-stat-value">
                {formatTokenCount(metadata.token_usage.total_tokens)}
              </span>
            </div>
          </div>
          {metadata.token_usage.by_section && Object.keys(metadata.token_usage.by_section).length > 0 && (
            <div className="token-breakdown">
              <h4>By Section</h4>
              <table className="token-table">
                <thead>
                  <tr>
                    <th>Section</th>
                    <th>Input</th>
                    <th>Output</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(metadata.token_usage.by_section).map(([section, usage]) => (
                    <tr key={section}>
                      <td>{section}</td>
                      <td>{formatTokenCount(usage.input_tokens)}</td>
                      <td>{formatTokenCount(usage.output_tokens)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Report Content - Using ReportPreview component */}
      <div className="bench-report-content">
        <ReportPreview
          reportData={reportData}
          selectedSections={{
            estructura_balance: true,
            indicadores_financieros: true,
            anexo_1: true,
          }}
        />
      </div>
    </div>
  );
}

export default function BenchReportViewer() {
  return (
    <AnalystBenchProvider>
      <BenchReportViewerContent />
    </AnalystBenchProvider>
  );
}
